/**
 * 출석 모니터링 페이지
 * Attendance Monitoring Page
 *
 * 실시간 미출석 학생 목록, 연락 로그 생성/관리 기능 제공
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../config/api';
import './AttendanceMonitoringPage.css';
import { getTodayKST } from '../../../utils/dateUtils';

interface AbsentStudent {
  user_id: string;
  name: string;
  phone?: string;
  expected_time: string;
  grace_period_minutes?: number;
  parent_phone?: string;
  schedule_id?: number;
  schedule_name?: string;
  organization_id?: string;
  contact_date?: string;
  contact_status?: string | null;
}

interface ContactLog {
  id: number;
  user_id: string;
  student_name: string;
  contact_date: string;
  contact_time?: string;
  contact_type: 'student_phone' | 'parent_phone' | 'other';
  status: 'pending' | 'contacted' | 'no_answer' | 'resolved';
  response_note?: string;
  created_at?: string;
  // 2025-11-02: 3회 연락 시도 시스템 추가
  attempt_count: number;
  max_attempts: number;
  final_status?: 'contacted' | 'resolved' | 'unreachable' | 'no_answer';
  attempts?: ContactAttempt[];
}

interface ContactAttempt {
  id: number;
  contact_log_id: number;
  attempt_number: number;
  contacted_at: string;
  contact_type: 'student_phone' | 'parent_phone' | 'other';
  phone_number?: string;
  status: 'contacted' | 'no_answer' | 'resolved';
  response_note?: string;
  contacted_by?: string;
  created_at: string;
}

interface LateStudent {
  id: number;
  student_id: string;
  student_name: string;
  student_phone?: string;
  record_date: string;
  expected_time: string;
  actual_time: string;
  minutes_late: number;
  block_start_time?: string;
  penalty_issued: boolean;
  status?: 'entered_late' | 'not_arrived';
  created_at?: string;
}

interface ContactLogCreate {
  user_id: string;
  contact_date: string;
  expected_time: string;
  schedule_id?: number;
  contact_type: 'student_phone' | 'parent_phone' | 'other';
  phone_number?: string;
  status: 'pending' | 'contacted' | 'no_answer' | 'resolved';
  response_note?: string;
}

interface Statistics {
  total_absent: number;
  contacted: number;
  resolved: number;
  pending: number;
  no_answer: number;
}

// 주간 스케줄 (학부모가 설정한 예상 입실/퇴실 시간)
interface StudySchedule {
  id: string;
  student_id: string;
  student_name?: string;
  day_of_week: number;
  // 다양한 필드명 지원 (API 응답 호환)
  expected_arrival?: string;
  expected_departure?: string;
  expected_arrival_time?: string;
  expected_departure_time?: string;
  expected_check_in?: string;
  expected_check_out?: string;
  is_active: boolean;
}

// 교시 블록 (연속 교시 → 입실/퇴실 시간)
interface PeriodBlock {
  student_id: string;
  student_name: string;
  day_of_week: number;
  periods: number[];
  start_time: string;  // 블록 시작 = 입실 시간
  end_time: string;     // 블록 끝 = 퇴실 시간
}

interface TodayExpectedSchedule {
  student_id: string;
  student_name: string;
  expected_arrival: string;
  expected_departure: string;
}

// 시간별 타임테이블 항목
interface TimeSlot {
  time: string;
  arrivals: string[];  // 입실 학생 이름 목록
  departures: string[];  // 퇴실 학생 이름 목록
}

const AttendanceMonitoringPage: React.FC = () => {
  const [organizationId, setOrganizationId] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [absentStudents, setAbsentStudents] = useState<AbsentStudent[]>([]);
  const [contactLogs, setContactLogs] = useState<ContactLog[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    total_absent: 0,
    contacted: 0,
    resolved: 0,
    pending: 0,
    no_answer: 0,
  });

  // 지각 학생 상태
  const [lateStudents, setLateStudents] = useState<LateStudent[]>([]);
  const [loadingLateStudents, setLoadingLateStudents] = useState<boolean>(false);

  // 주간 스케줄 (오늘 예상 입실/퇴실) 상태
  const [todayExpectedSchedules, setTodayExpectedSchedules] = useState<TodayExpectedSchedule[]>([]);
  const [loadingStudySchedules, setLoadingStudySchedules] = useState<boolean>(false);

  // 필터 상태
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterContactType, setFilterContactType] = useState<string>('all');
  const [filterDateRange, setFilterDateRange] = useState<string>('today');

  // 출결 이상 학생 카드 접힘 상태 (연락 시도 후 접힘)
  const [collapsedCardIds, setCollapsedCardIds] = useState<Set<string>>(new Set());
  const toggleCardCollapse = (studentId: string) => {
    setCollapsedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  // 모달 상태
  const [showContactModal, setShowContactModal] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<AbsentStudent | null>(null);
  const [contactForm, setContactForm] = useState<ContactLogCreate>({
    user_id: '',
    contact_date: getTodayKST(),
    expected_time: '09:00:00',
    contact_type: 'student_phone',
    status: 'contacted',
    response_note: '',
  });

  // 수정 모달 상태
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingLog, setEditingLog] = useState<ContactLog | null>(null);
  const [editForm, setEditForm] = useState<{
    contact_type?: 'student_phone' | 'parent_phone' | 'other';
    phone_number?: string;
    status?: 'pending' | 'contacted' | 'no_answer' | 'resolved';
    response_note?: string;
  }>({});

  // 2025-11-02: 연락 시도 추가 모달 상태
  const [showAddAttemptModal, setShowAddAttemptModal] = useState<boolean>(false);
  const [selectedLogForAttempt, setSelectedLogForAttempt] = useState<ContactLog | null>(null);
  const [attemptForm, setAttemptForm] = useState<{
    contact_type: 'student_phone' | 'parent_phone' | 'other';
    phone_number?: string;
    status: 'contacted' | 'no_answer' | 'resolved';
    response_note?: string;
  }>({
    contact_type: 'student_phone',
    status: 'no_answer',
    response_note: '',
  });

  // 2025-11-02: 연락 시도 이력 보기 모달 상태
  const [showAttemptsModal, setShowAttemptsModal] = useState<boolean>(false);
  const [selectedLogForHistory, setSelectedLogForHistory] = useState<ContactLog | null>(null);
  const [attemptHistory, setAttemptHistory] = useState<ContactAttempt[]>([]);

  // 회원 메모 상태
  const [studentMemos, setStudentMemos] = useState<Record<string, {
    id: string;
    content: string;
    category: string;
    author_name: string;
    created_at: string;
    is_pinned: boolean;
  }>>({});
  const [expandedMemoStudentId, setExpandedMemoStudentId] = useState<string | null>(null);
  const [expandedMemos, setExpandedMemos] = useState<Array<{
    id: string;
    content: string;
    category: string;
    author_name: string;
    created_at: string;
    is_pinned: boolean;
  }>>([]);
  const [loadingExpandedMemos, setLoadingExpandedMemos] = useState(false);

  // 로딩 및 에러 상태
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // 조직 정보 로드
  useEffect(() => {
    loadOrganizationInfo();
  }, []);

  // 자동 새로고침 (60초마다)
  useEffect(() => {
    if (organizationId) {
      const interval = setInterval(() => {
        loadAbsentStudents();
        loadContactLogs();
        loadStudySchedules();
        loadLateStudents();
      }, 60000); // 60초

      return () => clearInterval(interval);
    }
  }, [organizationId, targetDate]);

  // 데이터 로드
  useEffect(() => {
    if (organizationId) {
      loadAbsentStudents();
      loadContactLogs();
      loadStudySchedules();
      loadLateStudents();
    }
  }, [organizationId, targetDate]);

  // 통계 계산
  useEffect(() => {
    calculateStatistics();
  }, [absentStudents, contactLogs]);

  const loadOrganizationInfo = async () => {
    try {
      // JWT 토큰에서 직접 organization_id 추출
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      if (authToken) {
        try {
          const parts = authToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            if (payload.organization_id) {
              setOrganizationId(payload.organization_id);
              return;
            }
          }
        } catch (e) {
          console.error('JWT 파싱 실패:', e);
        }
      }

      setError('조직 정보를 찾을 수 없습니다.');
    } catch (err) {
      console.error('조직 정보 로딩 실패:', err);
      setError('조직 정보를 가져오는 중 오류가 발생했습니다.');
    }
  };

  const loadAbsentStudents = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/absent-students?organization_id=${organizationId}&target_date=${targetDate}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAbsentStudents(data);
        // 미출석 학생들의 최근 메모 로드
        if (data.length > 0) {
          loadRecentMemos(data.map((s: AbsentStudent) => s.user_id));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '미출석 학생 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('미출석 학생 로딩 실패:', err);
      setError('미출석 학생 목록을 가져오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentMemos = async (studentIds: string[]) => {
    try {
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/owner/member-memos/recent?student_ids=${studentIds.join(',')}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStudentMemos(prev => ({ ...prev, ...result.data }));
        }
      }
    } catch (err) {
      console.error('메모 로딩 실패:', err);
    }
  };

  const loadStudentAllMemos = async (studentId: string) => {
    setLoadingExpandedMemos(true);
    try {
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/owner/member-memos?student_id=${studentId}&limit=10&offset=0`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setExpandedMemos(result.data.memos);
        }
      }
    } catch (err) {
      console.error('메모 전체 로딩 실패:', err);
    } finally {
      setLoadingExpandedMemos(false);
    }
  };

  const MEMO_CATS: Record<string, string> = {
    general: '일반', academic: '학업', behavior: '행동',
    health: '건강', family: '가정', other: '기타',
  };

  const loadContactLogs = async () => {
    try {
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/contact-logs?organization_id=${organizationId}&target_date=${targetDate}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // AttendanceContactLogListResponse 형식: {total, items, page, page_size}
        setContactLogs(Array.isArray(data) ? data : (data.items || []));
      } else {
        console.error('연락 로그를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('연락 로그 로딩 실패:', err);
    }
  };

  // 주간 스케줄 (오늘 예상 입실/퇴실 시간) 로드
  const loadStudySchedules = async () => {
    try {
      setLoadingStudySchedules(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/student-schedules/organization/all`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('[DEBUG] Study schedules API response:', result);
        if (result.success && result.data) {
          // 오늘 요일에 해당하는 스케줄만 필터링
          // "YYYY-MM-DD" 문자열을 로컬 시간으로 파싱 (UTC 파싱 방지)
          const [year, month, day] = targetDate.split('-').map(Number);
          const today = new Date(year, month - 1, day);
          const jsDayOfWeek = today.getDay(); // JS: 0=일요일, 1=월요일, ...
          // DB는 0=월요일, 1=화요일, ..., 5=토요일, 6=일요일
          const dayOfWeek = jsDayOfWeek === 0 ? 6 : jsDayOfWeek - 1;
          console.log(`[DEBUG] Target date: ${targetDate}, JS dayOfWeek: ${jsDayOfWeek}, DB dayOfWeek: ${dayOfWeek}`);

          const studySchedules = result.data.study_schedules || [];
          const periodBlocks: PeriodBlock[] = result.data.period_blocks || [];
          const students = result.data.students || [];
          console.log(`[DEBUG] Total study_schedules: ${studySchedules.length}, period_blocks: ${periodBlocks.length}, students: ${students.length}`);

          // 학생 ID -> 이름 매핑
          const studentMap = new Map<string, string>();
          students.forEach((s: { id: string; name: string }) => {
            studentMap.set(s.id, s.name);
          });

          // schedule_type에 따라 명시적으로 분기 (서버에서 전달)
          const scheduleType = result.data.schedule_type || 'weekly';
          const todayBlocks = periodBlocks.filter(
            (block: PeriodBlock) => block.day_of_week === dayOfWeek
          );

          let todaySchedules: TodayExpectedSchedule[];

          if (scheduleType === 'period' && todayBlocks.length > 0) {
            // period 모드: 교시 블록 데이터 사용
            todaySchedules = todayBlocks.map((block: PeriodBlock) => ({
              student_id: block.student_id,
              student_name: block.student_name || studentMap.get(block.student_id) || '알 수 없음',
              expected_arrival: block.start_time,
              expected_departure: block.end_time,
            }));
            console.log(`[DEBUG] Using period blocks (schedule_type=period) for day ${dayOfWeek}: ${todaySchedules.length} entries`);
          } else {
            // weekly 모드: study_schedules 사용
            todaySchedules = studySchedules
              .filter((schedule: StudySchedule) =>
                schedule.day_of_week === dayOfWeek && schedule.is_active
              )
              .map((schedule: StudySchedule) => ({
                student_id: schedule.student_id,
                student_name: studentMap.get(schedule.student_id) || schedule.student_name || '알 수 없음',
                expected_arrival: schedule.expected_arrival || schedule.expected_arrival_time || schedule.expected_check_in || '',
                expected_departure: schedule.expected_departure || schedule.expected_departure_time || schedule.expected_check_out || '',
              }));
            console.log(`[DEBUG] Using study_schedules (schedule_type=${scheduleType}) for day ${dayOfWeek}: ${todaySchedules.length}`);
          }

          setTodayExpectedSchedules(todaySchedules);
        }
      } else {
        console.error('주간 스케줄을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('주간 스케줄 로딩 실패:', err);
    } finally {
      setLoadingStudySchedules(false);
    }
  };

  // 지각 학생 목록 로드
  const loadLateStudents = async () => {
    try {
      setLoadingLateStudents(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/late-students?organization_id=${organizationId}&target_date=${targetDate}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const lateList = Array.isArray(data) ? data : [];
        setLateStudents(lateList);
        // 지각 학생들의 최근 메모도 로드
        if (lateList.length > 0) {
          loadRecentMemos(lateList.map((s: LateStudent) => s.student_id));
        }
      } else {
        console.error('지각 학생 목록을 불러오는데 실패했습니다.');
        setLateStudents([]);
      }
    } catch (err) {
      console.error('지각 학생 로딩 실패:', err);
      setLateStudents([]);
    } finally {
      setLoadingLateStudents(false);
    }
  };

  const calculateStatistics = () => {
    const stats: Statistics = {
      total_absent: absentStudents.length,
      contacted: 0,
      resolved: 0,
      pending: 0,
      no_answer: 0,
    };

    // 오늘 날짜의 연락 로그만 집계
    const todayLogs = contactLogs.filter(log => log.contact_date === targetDate);

    todayLogs.forEach(log => {
      switch (log.status) {
        case 'contacted':
          stats.contacted++;
          break;
        case 'resolved':
          stats.resolved++;
          break;
        case 'pending':
          stats.pending++;
          break;
        case 'no_answer':
          stats.no_answer++;
          break;
      }
    });

    setStatistics(stats);
  };

  // 시간별 타임테이블 생성
  const getTimeSlots = useCallback((): TimeSlot[] => {
    const timeMap = new Map<string, {
      arrivals: string[];
      departures: string[];
    }>();

    const initSlot = () => ({
      arrivals: [],
      departures: [],
    });

    // 입실/퇴실 스케줄 처리
    todayExpectedSchedules.forEach(schedule => {
      // 입실 시간 처리
      if (schedule.expected_arrival) {
        const arrivalTime = schedule.expected_arrival.substring(0, 5); // HH:MM
        if (!timeMap.has(arrivalTime)) {
          timeMap.set(arrivalTime, initSlot());
        }
        timeMap.get(arrivalTime)!.arrivals.push(schedule.student_name);
      }

      // 퇴실 시간 처리
      if (schedule.expected_departure) {
        const departureTime = schedule.expected_departure.substring(0, 5); // HH:MM
        if (!timeMap.has(departureTime)) {
          timeMap.set(departureTime, initSlot());
        }
        timeMap.get(departureTime)!.departures.push(schedule.student_name);
      }
    });

    // 시간순 정렬
    const sortedTimes = Array.from(timeMap.keys()).sort();

    return sortedTimes.map(time => ({
      time,
      arrivals: timeMap.get(time)!.arrivals,
      departures: timeMap.get(time)!.departures,
    }));
  }, [todayExpectedSchedules]);

  // 현재 시간과 비교하여 상태 반환
  const getTimeStatus = (timeStr: string): 'past' | 'current' | 'future' => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTotalMinutes = currentHour * 60 + currentMinute;

    const [hour, minute] = timeStr.split(':').map(Number);
    const slotTotalMinutes = hour * 60 + minute;

    // 30분 범위 내면 현재
    if (Math.abs(currentTotalMinutes - slotTotalMinutes) <= 30) {
      return 'current';
    }
    return slotTotalMinutes < currentTotalMinutes ? 'past' : 'future';
  };

  const handleContactClick = (student: AbsentStudent) => {
    setSelectedStudent(student);
    setContactForm({
      user_id: student.user_id,
      contact_date: student.contact_date || getTodayKST(),
      expected_time: student.expected_time,
      schedule_id: student.schedule_id,
      contact_type: student.parent_phone ? 'parent_phone' : 'student_phone',
      phone_number: student.parent_phone || student.phone || '',
      status: 'contacted',
      response_note: '',
    });
    setShowContactModal(true);
  };

  // 연락 로그 수정 버튼 클릭
  const handleEditClick = (log: ContactLog) => {
    setEditingLog(log);
    setEditForm({
      contact_type: log.contact_type,
      status: log.status,
      response_note: log.response_note || '',
    });
    setShowEditModal(true);
  };

  // 연락 로그 수정 제출
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLog) return;

    try {
      setSubmitLoading(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/contact-logs/${editingLog.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editForm)
        }
      );

      if (response.ok) {
        // 모달 닫기
        setShowEditModal(false);
        setEditingLog(null);
        setEditForm({});

        // 데이터 새로고침 (미출석 학생 + 연락 로그)
        await loadAbsentStudents();
        await loadContactLogs();
        alert('연락 로그가 수정되었습니다.');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '연락 로그 수정에 실패했습니다.');
      }
    } catch (err) {
      console.error('연락 로그 수정 실패:', err);
      alert('연락 로그를 수정하는 중 오류가 발생했습니다.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 2025-11-02: 연락 시도 추가 버튼 클릭
  const handleAddAttemptClick = (log: ContactLog) => {
    setSelectedLogForAttempt(log);
    setAttemptForm({
      contact_type: log.contact_type,
      status: 'no_answer',
      response_note: '',
    });
    setShowAddAttemptModal(true);
  };

  // 2025-11-02: 연락 시도 추가 제출
  const handleAddAttemptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLogForAttempt) return;

    try {
      setSubmitLoading(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/contact-logs/${selectedLogForAttempt.id}/attempts`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(attemptForm)
        }
      );

      if (response.ok) {
        const result = await response.json();

        // 모달 닫기
        setShowAddAttemptModal(false);
        setSelectedLogForAttempt(null);
        setAttemptForm({
          contact_type: 'student_phone',
          status: 'no_answer',
          response_note: '',
        });

        // 데이터 새로고침
        await loadContactLogs();

        // 결과 메시지 표시
        let message = `${result.attempt_number}차 연락 시도가 등록되었습니다.`;
        if (result.final_status) {
          if (result.final_status === 'unreachable') {
            message += '\n\n⚠️ 3회 연락 시도 완료. 최종 상태: 연락 불가';
          } else if (result.final_status === 'contacted' || result.final_status === 'resolved') {
            message += '\n\n✅ 연락 완료! 추가 연락 시도가 필요 없습니다.';
          }
        }
        alert(message);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '연락 시도 추가에 실패했습니다.');
      }
    } catch (err) {
      console.error('연락 시도 추가 실패:', err);
      alert('연락 시도를 추가하는 중 오류가 발생했습니다.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 2025-11-02: 연락 시도 이력 보기
  const handleViewAttemptsClick = async (log: ContactLog) => {
    try {
      setSelectedLogForHistory(log);
      setShowAttemptsModal(true);

      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/contact-logs/${log.id}/attempts`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAttemptHistory(data.items || []);
      } else {
        console.error('연락 시도 이력을 불러오는데 실패했습니다.');
        setAttemptHistory([]);
      }
    } catch (err) {
      console.error('연락 시도 이력 로딩 실패:', err);
      setAttemptHistory([]);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    try {
      setSubmitLoading(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/attendance-monitoring/contact-logs`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...contactForm,
            organization_id: organizationId,
          })
        }
      );

      if (response.ok) {
        // ⭐ 연락 시도 완료 시 카드 자동 접기 — 다음 학생으로 시선 이동
        if (selectedStudent?.user_id) {
          setCollapsedCardIds(prev => new Set(prev).add(selectedStudent.user_id));
        }
        // 모달 닫기
        setShowContactModal(false);
        setSelectedStudent(null);
        setContactForm({
          user_id: '',
          contact_date: getTodayKST(),
          expected_time: '09:00:00',
          contact_type: 'student_phone',
          status: 'contacted',
          response_note: '',
        });

        // 데이터 새로고침 (미출석 학생 + 연락 로그)
        await loadAbsentStudents();
        await loadContactLogs();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '연락 로그 생성에 실패했습니다.');
      }
    } catch (err) {
      console.error('연락 로그 생성 실패:', err);
      alert('연락 로그를 생성하는 중 오류가 발생했습니다.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const exportToExcel = () => {
    // 필터링된 로그를 CSV 형식으로 변환
    const filteredLogs = getFilteredContactLogs();

    const csvContent = [
      ['학생명', '연락일', '연락시간', '연락방법', '상태', '응답내용'],
      ...filteredLogs.map(log => [
        log.student_name,
        log.contact_date,
        log.contact_time || '',
        getContactTypeLabel(log.contact_type),
        getStatusLabel(log.status),
        log.response_note || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `contact_logs_${targetDate}.csv`;
    link.click();
  };

  const getFilteredContactLogs = (): ContactLog[] => {
    let filtered = [...contactLogs];

    // 날짜 범위 필터
    if (filterDateRange === 'today') {
      filtered = filtered.filter(log => log.contact_date === targetDate);
    } else if (filterDateRange === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(log => new Date(log.contact_date) >= weekAgo);
    }

    // 상태 필터
    if (filterStatus !== 'all') {
      filtered = filtered.filter(log => log.status === filterStatus);
    }

    // 연락 방법 필터
    if (filterContactType !== 'all') {
      filtered = filtered.filter(log => log.contact_type === filterContactType);
    }

    return filtered.sort((a, b) =>
      new Date(b.contact_date + ' ' + (b.contact_time || '00:00')).getTime() -
      new Date(a.contact_date + ' ' + (a.contact_time || '00:00')).getTime()
    );
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: '대기',
      contacted: '연락완료',
      no_answer: '부재중',
      resolved: '해결',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: string): string => {
    const classes: Record<string, string> = {
      pending: 'status-badge-pending',
      contacted: 'status-badge-contacted',
      no_answer: 'status-badge-no-answer',
      resolved: 'status-badge-resolved',
    };
    return classes[status] || 'status-badge-default';
  };

  const getContactTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      student_phone: '학생 전화',
      parent_phone: '학부모 전화',
      other: '기타',
    };
    return labels[type] || type;
  };

  if (error && !organizationId) {
    return (
      <div className="attendance-monitoring-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-monitoring-page">
      <div className="page-header">
        <h1 className="page-title">📞 출석 모니터링</h1>
        <div className="header-controls">
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className="date-picker"
          />
          <button onClick={exportToExcel} className="export-button">
            📊 엑셀 내보내기
          </button>
        </div>
      </div>

      {/* 통계 대시보드 */}
      <div className="statistics-dashboard">
        <div className="stat-card total">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-label">총 미출석</div>
            <div className="stat-value">{statistics.total_absent}명</div>
          </div>
        </div>

        <div className="stat-card contacted">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">연락완료</div>
            <div className="stat-value">{statistics.contacted}명</div>
          </div>
        </div>

        <div className="stat-card resolved">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-label">해결</div>
            <div className="stat-value">{statistics.resolved}명</div>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-label">대기</div>
            <div className="stat-value">{statistics.pending}명</div>
          </div>
        </div>

        <div className="stat-card no-answer">
          <div className="stat-icon">📵</div>
          <div className="stat-content">
            <div className="stat-label">부재중</div>
            <div className="stat-value">{statistics.no_answer}명</div>
          </div>
        </div>
      </div>


      {/* 출결 이상 학생 (미출석 + 지각 통합) */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">⚠️ 출결 이상 학생</h2>
          <button onClick={() => { loadAbsentStudents(); loadLateStudents(); }} className="refresh-button">
            🔄 새로고침
          </button>
        </div>

        {loading ? (
          <div className="loading-state">로딩 중...</div>
        ) : (() => {
          // 미출석 + 지각 학생 통합 (중복 제거)
          const mergedMap = new Map<string, {
            name: string;
            studentId: string;
            expectedTime: string;
            actualTime?: string;
            minutesLate?: number;
            status: 'not_arrived' | 'entered_late';
            penaltyIssued: boolean;
            contactStatus?: string | null;
            parentPhone?: string;
            phone?: string;
            scheduleId?: number;
            gracePeriod?: number;
            absentData?: AbsentStudent;
          }>();

          // 1. 지각 학생 먼저 (더 정확한 정보)
          lateStudents.forEach(ls => {
            mergedMap.set(ls.student_id, {
              name: ls.student_name,
              studentId: ls.student_id,
              expectedTime: ls.expected_time,
              actualTime: ls.actual_time !== '-' ? ls.actual_time : undefined,
              minutesLate: ls.minutes_late,
              status: ls.status || 'not_arrived',
              penaltyIssued: ls.penalty_issued,
            });
          });

          // 2. 미출석 학생 병합 (연락 정보 추가)
          absentStudents.forEach(as_ => {
            const existing = mergedMap.get(as_.user_id);
            if (existing) {
              // 이미 있으면 연락 정보만 추가
              existing.contactStatus = as_.contact_status;
              existing.parentPhone = as_.parent_phone;
              existing.phone = as_.phone;
              existing.scheduleId = as_.schedule_id;
              existing.gracePeriod = as_.grace_period_minutes;
              existing.absentData = as_;
            } else {
              // 없으면 새로 추가 (미출석만 있는 학생)
              mergedMap.set(as_.user_id, {
                name: as_.name,
                studentId: as_.user_id,
                expectedTime: as_.expected_time,
                status: 'not_arrived',
                penaltyIssued: false,
                contactStatus: as_.contact_status,
                parentPhone: as_.parent_phone,
                phone: as_.phone,
                scheduleId: as_.schedule_id,
                gracePeriod: as_.grace_period_minutes,
                absentData: as_,
              });
            }
          });

          const mergedList = Array.from(mergedMap.values()).sort((a, b) => {
            // 미입실 먼저, 그 다음 늦은 입실
            if (a.status !== b.status) return a.status === 'not_arrived' ? -1 : 1;
            // 같은 상태면 지각 분 내림차순
            return (b.minutesLate || 0) - (a.minutesLate || 0);
          });

          if (mergedList.length === 0) {
            return (
              <div className="empty-state">
                <p>출결 이상 학생이 없습니다.</p>
              </div>
            );
          }

          // ⭐ 그룹핑: 미처리(연락 안함) → 처리중(pending) → 완료(contacted/resolved/no_answer)
          const groupOrder = (s: typeof mergedList[0]): number => {
            const cs = s.contactStatus;
            if (!cs) return 0;          // 미처리
            if (cs === 'pending') return 1; // 처리중
            return 2;                    // 완료
          };
          mergedList.sort((a, b) => {
            const ga = groupOrder(a), gb = groupOrder(b);
            if (ga !== gb) return ga - gb;
            if (a.status !== b.status) return a.status === 'not_arrived' ? -1 : 1;
            return (b.minutesLate || 0) - (a.minutesLate || 0);
          });

          // ⭐ 펼친(active) vs 접힌(collapsed) 카드 분리 — CSS Grid 행 정렬 문제 회피
          const activeList = mergedList.filter(s => !collapsedCardIds.has(s.studentId));
          const collapsedList = mergedList.filter(s => collapsedCardIds.has(s.studentId));

          return (
            <>
            {/* 접힌 카드들: 한 줄씩 슬림 리스트 (위쪽 작게) */}
            {collapsedList.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {collapsedList.map((student) => {
                  const cs = student.contactStatus;
                  const isContactDone = cs === 'contacted' || cs === 'resolved' || cs === 'no_answer';
                  return (
                    <div
                      key={student.studentId}
                      onClick={() => toggleCardCollapse(student.studentId)}
                      style={{
                        borderLeft: `4px solid ${student.status === 'not_arrived' ? '#dc2626' : '#f59e0b'}`,
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '6px 12px',
                        cursor: 'pointer',
                        opacity: isContactDone ? 0.7 : 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>▸ {student.name}</span>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                          예정 {student.expectedTime.substring(0, 5)}
                        </span>
                        {student.minutesLate != null && student.minutesLate > 0 && (
                          <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                            {student.minutesLate}분 지각
                          </span>
                        )}
                      </div>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 10.5,
                        fontWeight: 600,
                        background: cs === 'contacted' ? '#dcfce7' :
                                   cs === 'resolved' ? '#dbeafe' :
                                   cs === 'no_answer' ? '#fee2e2' : '#fff3cd',
                        color: cs === 'contacted' ? '#166534' :
                               cs === 'resolved' ? '#1e40af' :
                               cs === 'no_answer' ? '#991b1b' : '#854d0e',
                      }}>
                        {cs === 'contacted' ? '✓ 연락됨' :
                         cs === 'resolved' ? '✓ 해결' :
                         cs === 'no_answer' ? '✗ 부재중' :
                         cs === 'pending' ? '⋯ 연락중' : '접힘'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="students-grid">
              {activeList.map((student) => {
                return (
                <div key={student.studentId} className="student-card" style={{
                  borderLeft: `4px solid ${student.status === 'not_arrived' ? '#dc2626' : '#f59e0b'}`
                }}>
                  <div className="student-info">
                    <h3 className="student-name">{student.name}</h3>
                    <div className="student-details" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>
                        {student.status === 'not_arrived'
                          ? `예정 ${student.expectedTime.substring(0, 5)} | 미입실`
                          : `예정 ${student.expectedTime.substring(0, 5)} → 입실 ${(student.actualTime || '').substring(0, 5)}`
                        }
                      </span>
                      {student.minutesLate != null && student.minutesLate > 0 && (
                        <span style={{
                          color: '#dc2626',
                          fontWeight: 600,
                          fontSize: '14px'
                        }}>
                          {student.minutesLate}분 지각
                        </span>
                      )}
                      {student.parentPhone && (
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>
                          학부모: {student.parentPhone}
                        </span>
                      )}
                      {/* 최근 메모 */}
                      {studentMemos[student.studentId] && (
                        <div style={{
                          marginTop: '4px',
                          padding: '6px 8px',
                          background: '#f0f9ff',
                          border: '1px solid #bae6fd',
                          borderRadius: '6px',
                          fontSize: '12px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#0369a1', fontWeight: 600 }}>
                              📝 {studentMemos[student.studentId].content.length > 30
                                ? studentMemos[student.studentId].content.substring(0, 30) + '...'
                                : studentMemos[student.studentId].content}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (expandedMemoStudentId === student.studentId) {
                                  setExpandedMemoStudentId(null);
                                  setExpandedMemos([]);
                                } else {
                                  setExpandedMemoStudentId(student.studentId);
                                  loadStudentAllMemos(student.studentId);
                                }
                              }}
                              style={{
                                background: 'none', border: 'none', color: '#0369a1',
                                cursor: 'pointer', fontSize: '11px', textDecoration: 'underline',
                                padding: '0 4px',
                              }}
                            >
                              {expandedMemoStudentId === student.studentId ? '접기' : '더보기'}
                            </button>
                          </div>
                          <span style={{ color: '#64748b', fontSize: '11px' }}>
                            {studentMemos[student.studentId].author_name} · {new Date(studentMemos[student.studentId].created_at).toLocaleDateString('ko-KR')}
                          </span>
                        </div>
                      )}
                      {/* 메모 더보기 펼침 */}
                      {expandedMemoStudentId === student.studentId && (
                        <div style={{
                          marginTop: '4px',
                          padding: '8px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}>
                          {loadingExpandedMemos ? (
                            <div style={{ textAlign: 'center', color: '#64748b', fontSize: '12px', padding: '8px' }}>
                              로딩 중...
                            </div>
                          ) : expandedMemos.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {expandedMemos.map((memo) => (
                                <div key={memo.id} style={{
                                  padding: '6px 8px',
                                  background: memo.is_pinned ? '#fef9c3' : '#fff',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                }}>
                                  <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                                    {memo.is_pinned && <span>📌</span>}
                                    <span style={{
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      fontSize: '10px',
                                      background: '#f1f5f9',
                                      color: '#475569',
                                    }}>
                                      {MEMO_CATS[memo.category] || memo.category}
                                    </span>
                                    <span style={{ color: '#94a3b8', fontSize: '10px' }}>
                                      {memo.author_name} · {new Date(memo.created_at).toLocaleDateString('ko-KR')}
                                    </span>
                                  </div>
                                  <p style={{ color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{memo.content}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', padding: '8px' }}>
                              메모가 없습니다
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: student.status === 'not_arrived' ? '#fef2f2' : '#fffbeb',
                      color: student.status === 'not_arrived' ? '#991b1b' : '#92400e',
                    }}>
                      {student.status === 'not_arrived' ? '미입실' : '늦은 입실'}
                    </span>
                    {student.penaltyIssued && (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: '#fee2e2',
                        color: '#991b1b',
                      }}>
                        벌점 부과됨
                      </span>
                    )}
                    {student.contactStatus ? (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: student.contactStatus === 'contacted' ? '#dcfce7' :
                                   student.contactStatus === 'resolved' ? '#dbeafe' :
                                   student.contactStatus === 'no_answer' ? '#fee2e2' : '#f3f4f6',
                        color: student.contactStatus === 'contacted' ? '#166534' :
                               student.contactStatus === 'resolved' ? '#1e40af' :
                               student.contactStatus === 'no_answer' ? '#991b1b' : '#374151',
                      }}>
                        {student.contactStatus === 'contacted' ? '연락완료' :
                         student.contactStatus === 'resolved' ? '해결' :
                         student.contactStatus === 'no_answer' ? '부재중' :
                         student.contactStatus === 'pending' ? '대기' : student.contactStatus}
                      </span>
                    ) : student.absentData ? (
                      <button
                        onClick={() => handleContactClick(student.absentData!)}
                        className="contact-button"
                        style={{ fontSize: '12px', padding: '4px 10px' }}
                      >
                        연락하기
                      </button>
                    ) : null}
                    {/* 수동 접기 버튼 (이미 처리됐거나 보고 끝났을 때) */}
                    <button
                      onClick={() => toggleCardCollapse(student.studentId)}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        background: 'transparent',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        color: '#6b7280',
                        cursor: 'pointer',
                        marginTop: 2,
                      }}
                    >
                      접기
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
            </>
          );
        })()}
      </div>

      {/* 오늘 출결 기록 (누적) */}
      {(() => {
        const enteredLateRecords = lateStudents.filter(ls => ls.status === 'entered_late');
        if (enteredLateRecords.length === 0) return null;

        return (
          <div className="content-section">
            <div className="section-header">
              <h2 className="section-title">📋 오늘 출결 기록 ({enteredLateRecords.length}건)</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {enteredLateRecords.map((record, idx) => (
                <div key={record.id || idx} style={{
                  padding: '12px 16px',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 600 }}>🕐 {record.student_name}</span>
                    </div>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {record.expected_time?.substring(0, 5)} 예정 → {record.actual_time?.substring(0, 5)} 입실
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '14px' }}>
                      {record.minutes_late}분 지각
                    </span>
                    {record.penalty_issued && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: '#fee2e2',
                        color: '#991b1b',
                      }}>
                        벌점 부과됨
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 연락 로그 테이블 */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">📋 연락 로그</h2>
          <div className="filter-controls">
            <select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              className="filter-select"
            >
              <option value="today">오늘</option>
              <option value="week">최근 7일</option>
              <option value="all">전체</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">전체 상태</option>
              <option value="pending">대기</option>
              <option value="contacted">연락완료</option>
              <option value="no_answer">부재중</option>
              <option value="resolved">해결</option>
            </select>

            <select
              value={filterContactType}
              onChange={(e) => setFilterContactType(e.target.value)}
              className="filter-select"
            >
              <option value="all">전체 방법</option>
              <option value="student_phone">학생 전화</option>
              <option value="parent_phone">학부모 전화</option>
              <option value="other">기타</option>
            </select>
          </div>
        </div>

        <div className="contact-logs-table">
          <table>
            <thead>
              <tr>
                <th>학생명</th>
                <th>연락일</th>
                <th>연락시간</th>
                <th>연락방법</th>
                <th>상태</th>
                <th>🔁 시도 횟수</th>
                <th>최종 상태</th>
                <th>응답내용</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredContactLogs().map((log) => (
                <tr key={log.id}>
                  <td>{log.student_name}</td>
                  <td>{log.contact_date}</td>
                  <td>{log.contact_time || '-'}</td>
                  <td>{getContactTypeLabel(log.contact_type)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(log.status)}`}>
                      {getStatusLabel(log.status)}
                    </span>
                  </td>
                  <td>
                    {/* 2025-11-02: 시도 횟수 표시 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <strong>{log.attempt_count}/{log.max_attempts}</strong>
                      {log.attempt_count > 0 && (
                        <button
                          onClick={() => handleViewAttemptsClick(log)}
                          className="view-attempts-button"
                          title="이력 보기"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                          }}
                        >
                          📋
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    {/* 2025-11-02: 최종 상태 표시 */}
                    {log.final_status ? (
                      <span className={`status-badge ${getStatusBadgeClass(log.final_status)}`}>
                        {log.final_status === 'unreachable' ? '❌ 연락불가' : getStatusLabel(log.final_status)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="response-note">{log.response_note || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleEditClick(log)}
                        className="edit-button"
                        title="수정"
                      >
                        ✏️
                      </button>
                      {/* 2025-11-02: 연락 시도 추가 버튼 */}
                      {log.attempt_count < log.max_attempts && !log.final_status && (
                        <button
                          onClick={() => handleAddAttemptClick(log)}
                          className="add-attempt-button"
                          title="연락 시도 추가"
                          style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          +연락
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {getFilteredContactLogs().length === 0 && (
            <div className="empty-table-state">
              연락 로그가 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 연락 로그 생성 모달 */}
      {showContactModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>연락 로그 작성</h2>
              <button
                className="close-button"
                onClick={() => setShowContactModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="contact-form">
              <div className="form-group">
                <label>학생명</label>
                <input
                  type="text"
                  value={selectedStudent.name}
                  disabled
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>연락 방법</label>
                <select
                  value={contactForm.contact_type}
                  onChange={(e) =>
                    setContactForm({
                      ...contactForm,
                      contact_type: e.target.value as ContactLogCreate['contact_type'],
                    })
                  }
                  className="form-select"
                  required
                >
                  <option value="student_phone">학생 전화</option>
                  <option value="parent_phone">학부모 전화</option>
                  <option value="other">기타</option>
                </select>
              </div>

              <div className="form-group">
                <label>상태</label>
                <select
                  value={contactForm.status}
                  onChange={(e) =>
                    setContactForm({
                      ...contactForm,
                      status: e.target.value as ContactLogCreate['status'],
                    })
                  }
                  className="form-select"
                  required
                >
                  <option value="pending">대기</option>
                  <option value="contacted">연락완료</option>
                  <option value="no_answer">부재중</option>
                  <option value="resolved">해결</option>
                </select>
              </div>

              <div className="form-group">
                <label>응답 내용</label>
                <textarea
                  value={contactForm.response_note}
                  onChange={(e) =>
                    setContactForm({
                      ...contactForm,
                      response_note: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows={4}
                  placeholder="연락 내용 및 응답을 입력하세요..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  className="cancel-button"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="submit-button"
                >
                  {submitLoading ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 연락 로그 수정 모달 */}
      {showEditModal && editingLog && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>연락 로그 수정</h2>
              <button
                className="close-button"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="contact-form">
              <div className="form-group">
                <label>학생명</label>
                <input
                  type="text"
                  value={editingLog.student_name}
                  disabled
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>연락일</label>
                <input
                  type="text"
                  value={editingLog.contact_date}
                  disabled
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>연락 방법</label>
                <select
                  value={editForm.contact_type}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      contact_type: e.target.value as 'student_phone' | 'parent_phone' | 'other',
                    })
                  }
                  className="form-select"
                >
                  <option value="student_phone">학생 전화</option>
                  <option value="parent_phone">학부모 전화</option>
                  <option value="other">기타</option>
                </select>
              </div>

              <div className="form-group">
                <label>상태</label>
                <select
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      status: e.target.value as 'pending' | 'contacted' | 'no_answer' | 'resolved',
                    })
                  }
                  className="form-select"
                >
                  <option value="pending">대기</option>
                  <option value="contacted">연락완료</option>
                  <option value="no_answer">부재중</option>
                  <option value="resolved">해결</option>
                </select>
              </div>

              <div className="form-group">
                <label>응답 내용</label>
                <textarea
                  value={editForm.response_note}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      response_note: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows={4}
                  placeholder="연락 내용 및 응답을 입력하세요..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="cancel-button"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="submit-button"
                >
                  {submitLoading ? '수정 중...' : '수정'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2025-11-02: 연락 시도 추가 모달 */}
      {showAddAttemptModal && selectedLogForAttempt && (
        <div className="modal-overlay" onClick={() => setShowAddAttemptModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔁 연락 시도 추가 ({selectedLogForAttempt.attempt_count + 1}/{selectedLogForAttempt.max_attempts}차)</h2>
              <button
                className="close-button"
                onClick={() => setShowAddAttemptModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddAttemptSubmit} className="contact-form">
              <div className="form-group">
                <label>학생명</label>
                <input
                  type="text"
                  value={selectedLogForAttempt.student_name}
                  disabled
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>현재 시도 횟수</label>
                <input
                  type="text"
                  value={`${selectedLogForAttempt.attempt_count}/${selectedLogForAttempt.max_attempts}`}
                  disabled
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>연락 방법</label>
                <select
                  value={attemptForm.contact_type}
                  onChange={(e) =>
                    setAttemptForm({
                      ...attemptForm,
                      contact_type: e.target.value as 'student_phone' | 'parent_phone' | 'other',
                    })
                  }
                  className="form-select"
                  required
                >
                  <option value="student_phone">학생 전화</option>
                  <option value="parent_phone">학부모 전화</option>
                  <option value="other">기타</option>
                </select>
              </div>

              <div className="form-group">
                <label>연락 결과 *</label>
                <select
                  value={attemptForm.status}
                  onChange={(e) =>
                    setAttemptForm({
                      ...attemptForm,
                      status: e.target.value as 'contacted' | 'no_answer' | 'resolved',
                    })
                  }
                  className="form-select"
                  required
                >
                  <option value="contacted">✅ 연락완료</option>
                  <option value="no_answer">📵 부재중</option>
                  <option value="resolved">🎯 해결 (출석 확인)</option>
                </select>
              </div>

              <div className="form-group">
                <label>연락 내용</label>
                <textarea
                  value={attemptForm.response_note}
                  onChange={(e) =>
                    setAttemptForm({
                      ...attemptForm,
                      response_note: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows={4}
                  placeholder="연락 결과 및 내용을 입력하세요..."
                />
              </div>

              {selectedLogForAttempt.attempt_count + 1 >= selectedLogForAttempt.max_attempts && (
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  padding: '10px',
                  marginBottom: '15px',
                }}>
                  ⚠️ <strong>마지막 시도입니다!</strong> 이번 시도 후 더 이상 연락 시도를 추가할 수 없습니다.
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddAttemptModal(false)}
                  className="cancel-button"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="submit-button"
                >
                  {submitLoading ? '추가 중...' : '연락 시도 추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2025-11-02: 연락 시도 이력 모달 */}
      {showAttemptsModal && selectedLogForHistory && (
        <div className="modal-overlay" onClick={() => setShowAttemptsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 연락 시도 이력 - {selectedLogForHistory.student_name}</h2>
              <button
                className="close-button"
                onClick={() => setShowAttemptsModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {attemptHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  연락 시도 이력이 없습니다.
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                    총 {attemptHistory.length}회 시도 (최대 {selectedLogForHistory.max_attempts}회)
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>시도</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>시간</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>방법</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>결과</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>메모</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attemptHistory.map((attempt) => (
                        <tr key={attempt.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}>
                            <strong>{attempt.attempt_number}차</strong>
                          </td>
                          <td style={{ padding: '12px', fontSize: '13px' }}>
                            {new Date(attempt.contacted_at).toLocaleString('ko-KR')}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {getContactTypeLabel(attempt.contact_type)}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span className={`status-badge ${getStatusBadgeClass(attempt.status)}`}>
                              {getStatusLabel(attempt.status)}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontSize: '13px', maxWidth: '200px' }}>
                            {attempt.response_note || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {selectedLogForHistory.final_status && (
                    <div style={{
                      marginTop: '20px',
                      padding: '15px',
                      background: selectedLogForHistory.final_status === 'unreachable' ? '#fee' : '#efe',
                      borderRadius: '4px',
                      border: `1px solid ${selectedLogForHistory.final_status === 'unreachable' ? '#fcc' : '#cfc'}`,
                    }}>
                      <strong>최종 상태:</strong> {' '}
                      {selectedLogForHistory.final_status === 'unreachable' ? '❌ 연락 불가' :
                       selectedLogForHistory.final_status === 'contacted' ? '✅ 연락 완료' :
                       selectedLogForHistory.final_status === 'resolved' ? '🎯 해결' :
                       selectedLogForHistory.final_status}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: '0 20px 20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowAttemptsModal(false)}
                className="cancel-button"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceMonitoringPage;
